import { decodePrintSettings, type AqsiPrintSettings } from './printSettings'

import { baseApi, resourceListTag, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import { AQSI_WIRE, commandHeader, envelope } from '../../../shared/api/envelope'
import { AQSI_TAG_TYPES, AQSI_TAGS } from '../../../shared/api/tags'
import { AQSI_URLS, type SessionAction } from '../../../shared/api/urls'
import type { CommandIntent } from '../../../shared/model/ids'
import type {
  AqsiCashierProof,
  AqsiCashierRmkSettings,
  AqsiOperationStatus,
  AqsiPosCashiers,
  AqsiPriceType,
  AqsiRegisterLink,
  AqsiRegisters,
  AqsiSession,
  AqsiSessionClosed,
  AqsiSessionJournal,
  AqsiShift,
  PriceTypeRef,
  ShiftCommandKind,
} from '../model/types'
import {
  decodeCashierProof,
  decodeCashierRmkSettings,
  decodeOperationStatus,
  decodePosCashiers,
  decodePriceTypes,
  decodeOptionalSession,
  decodeRegisterLink,
  decodeRegisters,
  decodeSession,
  decodeSessionClosed,
  decodeSessionJournal,
  decodeShift,
} from './contracts'

/** Аргументы ресурсного запроса: rid — часть ключа кэша (D-064). */
export interface AqsiResourceArgs {
  resourceId: string
}

/** Касса кабинета (RP-C9): рабочих мест нет, касса — устройство `deviceId`. */
export interface AqsiRegisterArgs extends AqsiResourceArgs {
  deviceId: number
}

/** Кассир и его подтверждение `pos`: по ним служба Cashiers отдаёт его настройки РМК. */
export interface AqsiCashierRmkArgs extends AqsiResourceArgs {
  cashierId: string
  grant: string
}

export interface AqsiOperationArgs extends AqsiResourceArgs {
  operationId: string
}

/** Версия «кассы» в команде открытия работы — служба принимает только 1 (`AqsiRegisterWire.RegisterVersion`). */
export const REGISTER_VERSION = 1

export interface SessionOpenArgs extends AqsiRegisterArgs {
  intent: CommandIntent
  /** Кассир источника (`pos/cashiers`); нет — работа под своим именем (в источнике кассиров нет). */
  cashierId?: string
  /** Подтверждение кода кассира (RP-FB22 4.3): только если у кассира `login.kind ≠ none`. */
  cashierProof?: string
  /** «Работать под своим именем» (RP-FB22 4.6): нужно, когда в источнике нет кассиров; вместе с `cashierId` не передаётся. */
  asSelf?: boolean
  /** Вид цены работы: `null` — «Цена кассы»; не передан — служба берёт «Цену кассы». */
  priceType?: PriceTypeRef | null
}

/** Проверка кода кассира (RP-FB22 4.2): код живёт в аргументе одного запроса и нигде больше. */
export interface CashierVerifyArgs extends AqsiResourceArgs {
  cashierId: string
  credential: string
  intent: CommandIntent
}

export interface SessionOwnerArgs extends AqsiRegisterArgs {
  sessionId: string
  intent: CommandIntent
  /** Версия СЕССИИ. */
  expectedVersion: number
}

export interface SessionReasonArgs extends SessionOwnerArgs {
  reason: string
}

/** RP-FB11: вид цены своей работы; источник и ключ `null` — «Цена кассы». */
export interface SessionPriceTypeArgs extends SessionOwnerArgs {
  priceTypeResourceId: string | null
  priceTypeKey: string | null
}

export interface ShiftCommandArgs extends SessionOwnerArgs {
  kind: ShiftCommandKind
}

export interface ReconcileArgs extends AqsiOperationArgs {
  intent: CommandIntent
  /** Версия записи операции. */
  expectedVersion: number
}

/**
 * Ручная сверка команды смены с неизвестным исходом (UI-ACTIONS P1 №5): служба
 * принимает её и для смены (`AqsiManualResolutions`), кабинет не вызывается.
 */
export interface ShiftResolveArgs extends ReconcileArgs {
  outcome: 'Succeeded' | 'Failed'
  reason: string
  checked: string
}

const SESSION_KINDS: Readonly<Record<SessionAction, string>> = {
  open: 'aqsi.session.open',
  heartbeat: 'aqsi.session.heartbeat',
  close: 'aqsi.session.close',
  takeover: 'aqsi.session.takeover',
  'close-foreign': 'aqsi.session.close-foreign',
  'price-type': 'aqsi.session.price-type',
}

/** Что перечитать после команды над кассой ЭТОГО ресурса: список касс (занятость, смена, связь). */
const seatTags = (resourceId: string, deviceId?: number) => [
  resourceListTag(AQSI_TAGS.registers, resourceId),
  ...(deviceId !== undefined ? [resourceTag(AQSI_TAGS.journal, resourceId, String(deviceId))] : []),
]

/**
 * Кассовые ручки RP-C8/C9 в ОБЩЕМ `baseApi` (R04): имена `aqsi*`, rid в
 * аргументах и тегах. Команды не повторяются transport-ом (ADR-11): ответа нет —
 * экран перечитывает состояние, а не шлёт POST ещё раз.
 */
export const rmkApi = baseApi.enhanceEndpoints({ addTagTypes: AQSI_TAG_TYPES }).injectEndpoints({
  endpoints: (build) => ({
    aqsiPrintSettings: build.query<AqsiPrintSettings, AqsiRegisterArgs>({
      query: ({ resourceId, deviceId }) => ({
        url: AQSI_URLS.printSettings(resourceId, deviceId),
        decode: envelope('aqsi.print-settings', resourceId, (raw) => decodePrintSettings(raw, deviceId)),
      }),
      providesTags: (_result, _error, { resourceId, deviceId }) => [resourceTag(AQSI_TAGS.printSettings, resourceId, String(deviceId)), resourceScopeTag(resourceId)],
    }),
    aqsiUpdatePrintSettings: build.mutation<AqsiPrintSettings, AqsiRegisterArgs & { count: number; acceptQr: boolean; fastPayment: boolean; expectedVersion: number; intent: CommandIntent }>({
      query: ({ resourceId, deviceId, count, acceptQr, fastPayment, expectedVersion, intent }) => ({
        url: AQSI_URLS.printSettings(resourceId, deviceId), method: 'PUT',
        body: { ...commandHeader('aqsi.print-settings.update', resourceId, intent, expectedVersion), deviceId, printSlipCount: count, acceptQr, fastPayment },
        decode: envelope('aqsi.print-settings', resourceId, (raw) => decodePrintSettings(raw, deviceId)),
      }),
      invalidatesTags: (_result, _error, { resourceId, deviceId }) => [resourceTag(AQSI_TAGS.printSettings, resourceId, String(deviceId))],
    }),
    /** Кассы магазина источника — живое чтение кабинета (кэш службы 5 с, «Обновить» — мимо кэша). */
    aqsiRegisters: build.query<AqsiRegisters, AqsiResourceArgs>({
      query: ({ resourceId }) => ({
        url: AQSI_URLS.registers(resourceId),
        decode: envelope('aqsi.pos.registers', resourceId, decodeRegisters),
      }),
      providesTags: (_result, _error, { resourceId }) => [
        resourceListTag(AQSI_TAGS.registers, resourceId),
        resourceScopeTag(resourceId),
      ],
    }),
    /** Кассиры источника и какой вход нужен каждому (RP-FB22): живое чтение кабинета. */
    aqsiPosCashiers: build.query<AqsiPosCashiers, AqsiResourceArgs>({
      query: ({ resourceId }) => ({
        url: AQSI_URLS.posCashiers(resourceId),
        decode: envelope('aqsi.pos.cashiers', resourceId, decodePosCashiers),
      }),
      providesTags: (_result, _error, { resourceId }) => [
        resourceListTag(AQSI_TAGS.posCashiers, resourceId),
        resourceScopeTag(resourceId),
      ],
    }),
    /** Моя работа за кассой — наша база, кабинет не нужен: этап «смены» или «продажа» решает она. */
    aqsiMySession: build.query<AqsiSession | null, AqsiResourceArgs>({
      query: ({ resourceId }) => ({
        url: AQSI_URLS.sessionMine(resourceId),
        // Расписание повторов ведёт РМК: одна попытка — один HTTP-запрос.
        noRetry: true,
        decode: envelope('aqsi.session', resourceId, decodeOptionalSession),
      }),
      providesTags: (_result, _error, { resourceId }) => [
        resourceListTag(AQSI_TAGS.registers, resourceId),
        resourceScopeTag(resourceId),
      ],
    }),
    /** Виды цен продажи (RP-FB11) — общий каталог Platform, читается только при работе за кассой. */
    aqsiPriceTypes: build.query<readonly AqsiPriceType[], AqsiResourceArgs>({
      query: ({ resourceId }) => ({
        url: AQSI_URLS.priceTypes(),
        params: { resourceId },
        decode: decodePriceTypes,
      }),
      providesTags: (_result, _error, { resourceId }) => [resourceScopeTag(resourceId)],
    }),
    /**
     * Персональные настройки РМК выбранного кассира (RP-CT6): читаются по его подтверждению `pos`. Подтверждение в ключ кэша не входит:
     * оно обновляется, а настройки те же. Ошибка чтения не блокирует РМК — «Начало работы» остаётся с прежним выбором.
     */
    aqsiCashierRmkSettings: build.query<AqsiCashierRmkSettings, AqsiCashierRmkArgs>({
      query: ({ resourceId, grant }) => ({ url: AQSI_URLS.cashierRmkCurrent, params: { resourceId }, cashierGrant: grant, decode: decodeCashierRmkSettings, noRetry: true }),
      serializeQueryArgs: ({ queryArgs }) => `${queryArgs.resourceId}|${queryArgs.cashierId.toLowerCase()}`,
      providesTags: (_result, _error, { resourceId }) => [resourceScopeTag(resourceId)],
    }),
    /** Факт смены у кабинета — дорогое чтение (все страницы смен): только по запросу экрана. */
    aqsiShift: build.query<AqsiShift, AqsiRegisterArgs>({
      query: ({ resourceId, deviceId }) => ({
        url: AQSI_URLS.shift(resourceId, deviceId),
        noRetry: true,
        decode: envelope('aqsi.shift', resourceId, decodeShift),
      }),
      providesTags: (_result, _error, { resourceId, deviceId }) => [
        resourceTag(AQSI_TAGS.shift, resourceId, String(deviceId)),
        resourceScopeTag(resourceId),
      ],
    }),
    /** Статус команды из журнала службы — без обращения к кабинету. */
    aqsiOperation: build.query<AqsiOperationStatus, AqsiOperationArgs>({
      query: ({ resourceId, operationId }) => ({
        url: AQSI_URLS.operation(resourceId, operationId),
        decode: envelope('aqsi.operation.status', resourceId, decodeOperationStatus),
      }),
      providesTags: (_result, _error, { resourceId, operationId }) => [
        resourceTag(AQSI_TAGS.operation, resourceId, operationId),
        resourceScopeTag(resourceId),
      ],
    }),
    /**
     * Связь двух уровней (С3): сервер AQSI и аппарат кассы. Чтение кассе ничего не отправляет —
     * отдаёт итог последней проверки; в цикле не опрашивается.
     */
    aqsiRegisterLink: build.query<AqsiRegisterLink, AqsiRegisterArgs>({
      query: ({ resourceId, deviceId }) => ({
        url: AQSI_URLS.link(resourceId, deviceId),
        decode: envelope('aqsi.pos.link', resourceId, decodeRegisterLink),
      }),
      providesTags: (_result, _error, { resourceId, deviceId }) => [
        resourceTag(AQSI_TAGS.link, resourceId, String(deviceId)),
        resourceScopeTag(resourceId),
      ],
    }),
    /** Журнал кассы: кто садился, кто забирал (право на журнал). */
    aqsiSessionJournal: build.query<AqsiSessionJournal, AqsiRegisterArgs>({
      query: ({ resourceId, deviceId }) => ({
        url: AQSI_URLS.sessionJournal(resourceId, deviceId),
        params: { limit: 50 },
        decode: envelope('aqsi.session.journal', resourceId, decodeSessionJournal),
      }),
      providesTags: (_result, _error, { resourceId, deviceId }) => [
        resourceTag(AQSI_TAGS.journal, resourceId, String(deviceId)),
        resourceScopeTag(resourceId),
      ],
    }),

    aqsiSessionOpen: build.mutation<AqsiSession, SessionOpenArgs>({
      query: ({ resourceId, deviceId, cashierId, cashierProof, asSelf, priceType, intent }) => ({
        url: AQSI_URLS.session(resourceId, deviceId, 'open'),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: {
          ...commandHeader(SESSION_KINDS.open, resourceId, intent, REGISTER_VERSION),
          deviceId,
          ...(cashierId !== undefined ? { cashierId } : {}),
          ...(cashierProof !== undefined ? { cashierProof } : {}),
          ...(asSelf === true ? { asSelf: true } : {}),
          ...(priceType !== undefined ? { priceType } : {}),
        },
        decode: envelope('aqsi.session', resourceId, decodeSession),
      }),
      // И при отказе, и без ответа: состояние кассы могло измениться — перечитать ЧТЕНИЕМ.
      invalidatesTags: (_result, _error, { resourceId, deviceId }) => seatTags(resourceId, deviceId),
    }),
    /**
     * Проверка кода кассира. Каждая попытка считается службой (лимит неверных кодов), поэтому запрос не повторяется
     * сам; неверный код — отказ 403 с остатком попыток, лимит — 423 с временем ожидания.
     */
    aqsiCashierVerify: build.mutation<AqsiCashierProof, CashierVerifyArgs>({
      query: ({ resourceId, cashierId, credential, intent }) => ({
        url: AQSI_URLS.cashierVerify(resourceId, cashierId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: {
          ...commandHeader('aqsi.cashier.verify', resourceId, intent, REGISTER_VERSION),
          credential,
        },
        decode: envelope('aqsi.cashier.verified', resourceId, decodeCashierProof),
      }),
    }),
    /** Пульс: только время связи. Отказ значит, что сессию забрали или закрыли, — перечитать кассы. */
    aqsiSessionHeartbeat: build.mutation<AqsiSession, SessionOwnerArgs>({
      query: ({ resourceId, deviceId, sessionId, intent, expectedVersion }) => ({
        url: AQSI_URLS.session(resourceId, deviceId, 'heartbeat'),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: { ...commandHeader(SESSION_KINDS.heartbeat, resourceId, intent, expectedVersion), deviceId, sessionId },
        decode: envelope('aqsi.session', resourceId, decodeSession),
        timeoutMs: 15_000,
      }),
      invalidatesTags: (_result, error, { resourceId, deviceId }) =>
        error !== undefined ? seatTags(resourceId, deviceId) : [],
    }),
    aqsiSessionClose: build.mutation<AqsiSessionClosed, SessionOwnerArgs>({
      query: ({ resourceId, deviceId, sessionId, intent, expectedVersion }) => ({
        url: AQSI_URLS.session(resourceId, deviceId, 'close'),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: { ...commandHeader(SESSION_KINDS.close, resourceId, intent, expectedVersion), deviceId, sessionId },
        decode: envelope('aqsi.session.closed', resourceId, decodeSessionClosed),
      }),
      invalidatesTags: (_result, _error, { resourceId, deviceId }) => seatTags(resourceId, deviceId),
    }),
    /** RP-FB11: вид цены своей работы. Цены уже набранной корзины пересчитает её следующее изменение. */
    aqsiSessionPriceType: build.mutation<AqsiSession, SessionPriceTypeArgs>({
      query: ({ resourceId, deviceId, sessionId, intent, expectedVersion, priceTypeResourceId, priceTypeKey }) => ({
        url: AQSI_URLS.session(resourceId, deviceId, 'price-type'),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: {
          ...commandHeader(SESSION_KINDS['price-type'], resourceId, intent, expectedVersion),
          deviceId,
          sessionId,
          priceTypeResourceId,
          priceTypeKey,
        },
        decode: envelope('aqsi.session', resourceId, decodeSession),
      }),
      invalidatesTags: (_result, _error, { resourceId, deviceId }) => seatTags(resourceId, deviceId),
    }),
    aqsiSessionTakeover: build.mutation<AqsiSession, SessionReasonArgs>({
      query: ({ resourceId, deviceId, sessionId, intent, expectedVersion, reason }) => ({
        url: AQSI_URLS.session(resourceId, deviceId, 'takeover'),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: {
          ...commandHeader(SESSION_KINDS.takeover, resourceId, intent, expectedVersion),
          deviceId,
          sessionId,
          reason,
        },
        decode: envelope('aqsi.session', resourceId, decodeSession),
      }),
      invalidatesTags: (_result, _error, { resourceId, deviceId }) => seatTags(resourceId, deviceId),
    }),
    aqsiSessionCloseForeign: build.mutation<AqsiSessionClosed, SessionReasonArgs>({
      query: ({ resourceId, deviceId, sessionId, intent, expectedVersion, reason }) => ({
        url: AQSI_URLS.session(resourceId, deviceId, 'close-foreign'),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: {
          ...commandHeader(SESSION_KINDS['close-foreign'], resourceId, intent, expectedVersion),
          deviceId,
          sessionId,
          reason,
        },
        decode: envelope('aqsi.session.closed', resourceId, decodeSessionClosed),
      }),
      invalidatesTags: (_result, _error, { resourceId, deviceId }) => seatTags(resourceId, deviceId),
    }),
    /** Принять смену, открытую вне РМК: служба сначала читает факт у кабинета. */
    aqsiShiftAdopt: build.mutation<AqsiSession, SessionOwnerArgs>({
      query: ({ resourceId, deviceId, sessionId, intent, expectedVersion }) => ({
        url: AQSI_URLS.shiftCommand(resourceId, deviceId, 'adopt'),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: { ...commandHeader('aqsi.shift.adopt', resourceId, intent, expectedVersion), deviceId, sessionId },
        decode: envelope('aqsi.session', resourceId, decodeSession),
      }),
      invalidatesTags: (_result, _error, { resourceId, deviceId }) => [
        ...seatTags(resourceId, deviceId),
        resourceTag(AQSI_TAGS.shift, resourceId, String(deviceId)),
      ],
    }),
    /**
     * Открыть/закрыть смену: 202 и статус операции. Итог читается по
     * `operationId` (GET), команда не повторяется.
     */
    aqsiShiftCommand: build.mutation<AqsiOperationStatus, ShiftCommandArgs>({
      query: ({ resourceId, deviceId, sessionId, intent, expectedVersion, kind }) => ({
        url: AQSI_URLS.shiftCommand(resourceId, deviceId, kind),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: {
          ...commandHeader(
            kind === 'open' ? 'aqsi.shift.open' : 'aqsi.shift.close',
            resourceId,
            intent,
            expectedVersion,
          ),
          deviceId,
          sessionId,
        },
        decode: envelope('aqsi.operation.status', resourceId, decodeOperationStatus),
        // Служба читает факт смены у кабинета ДО команды, затем ждёт очередь ресурса.
        timeoutMs: 60_000,
      }),
      invalidatesTags: (_result, _error, { resourceId, deviceId }) => [
        ...seatTags(resourceId, deviceId),
        resourceTag(AQSI_TAGS.shift, resourceId, String(deviceId)),
      ],
    }),
    /** Сверка: служба только ЧИТАЕТ кабинет (операция и факт смены), ни одного POST к кассе. */
    aqsiOperationReconcile: build.mutation<AqsiOperationStatus, ReconcileArgs>({
      query: ({ resourceId, operationId, intent, expectedVersion }) => ({
        url: AQSI_URLS.operationReconcile(resourceId, operationId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: {
          kind: 'aqsi.operation.reconcile',
          resourceId,
          contractVersion: AQSI_WIRE.contractVersion,
          schemaVersion: AQSI_WIRE.schemaVersion,
          requestId: intent.operationId,
          idempotencyKey: intent.idempotencyKey,
          expectedVersion,
        },
        decode: envelope('aqsi.operation.status', resourceId, decodeOperationStatus),
        timeoutMs: 60_000,
      }),
      // Отказ сверки (версия, 503) — перечитать операцию: следующая сверка уйдёт с актуальной версией.
      invalidatesTags: (_result, error, { resourceId, operationId }) =>
        error !== undefined ? [resourceTag(AQSI_TAGS.operation, resourceId, operationId)] : [],
    }),
    aqsiShiftOperationResolve: build.mutation<AqsiOperationStatus, ShiftResolveArgs>({
      query: ({ resourceId, operationId, intent, expectedVersion, outcome, reason, checked }) => ({
        url: AQSI_URLS.operationResolve(resourceId, operationId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: {
          kind: 'aqsi.operation.resolve',
          resourceId,
          contractVersion: AQSI_WIRE.contractVersion,
          schemaVersion: AQSI_WIRE.schemaVersion,
          requestId: intent.operationId,
          idempotencyKey: intent.idempotencyKey,
          expectedVersion,
          outcome,
          reason,
          checked,
        },
        decode: envelope('aqsi.operation.status', resourceId, decodeOperationStatus),
      }),
      invalidatesTags: (_result, _error, { resourceId, operationId }) => [
        resourceTag(AQSI_TAGS.operation, resourceId, operationId),
        ...seatTags(resourceId),
      ],
    }),
    /**
     * «Проверить кассу» (С3): служба шлёт аппарату одну служебную операцию (чтение ФН — ничего не
     * печатает) и отвечает итогом двух уровней. Проверка аппарата за флагом службы: выключена — ответ
     * «не проверяется», кассе ничего не отправлено. Не чаще раза в 30 с на кассу (служба вернёт
     * прошлый итог с `cached`); не повторяется: без ответа экран перечитывает связь чтением.
     */
    aqsiRegisterLinkCheck: build.mutation<AqsiRegisterLink, AqsiRegisterArgs>({
      query: ({ resourceId, deviceId }) => ({
        url: AQSI_URLS.linkCheck(resourceId, deviceId),
        method: 'POST',
        body: {
          kind: 'aqsi.device.check',
          resourceId,
          contractVersion: AQSI_WIRE.contractVersion,
          schemaVersion: AQSI_WIRE.schemaVersion,
        },
        decode: envelope('aqsi.pos.link', resourceId, decodeRegisterLink),
        // Срок проверки на кассе (до 25 с по умолчанию, настраивается службой) плюс очередь кабинета ресурса.
        timeoutMs: 150_000,
      }),
      invalidatesTags: (_result, _error, { resourceId, deviceId }) => [
        resourceTag(AQSI_TAGS.link, resourceId, String(deviceId)),
        resourceListTag(AQSI_TAGS.registers, resourceId),
      ],
    }),
  }),
})

export const {
  aqsiPrintSettings,
  aqsiUpdatePrintSettings,
  aqsiRegisters,
  aqsiMySession,
  aqsiPosCashiers,
  aqsiCashierVerify,
  aqsiRegisterLink,
  aqsiRegisterLinkCheck,
  aqsiSessionJournal,
  aqsiShift,
  aqsiOperation,
  aqsiSessionOpen,
  aqsiSessionHeartbeat,
  aqsiSessionClose,
  aqsiSessionTakeover,
  aqsiSessionPriceType,
  aqsiPriceTypes,
  aqsiCashierRmkSettings,
  aqsiSessionCloseForeign,
  aqsiShiftAdopt,
  aqsiShiftCommand,
  aqsiOperationReconcile,
  aqsiShiftOperationResolve,
} = rmkApi.endpoints
